<template>
  <div>
    <BittsLoading :is-loading="isLoading" :with-transition="false">
      <div class="flex flex-col gap-20">
        <BittsBreadcrumbs
          :crumbs="breadcrumbs"
          :active-crumb-index="1"
          :last-crumb-index="1"
          :show-root="false"
          @breadcrumb-clicked="handleBreadcrumbClick"
        />
        <PageTitle
          :title="population?.name ?? ''"
          class="mb-0"
          subtitle="Manage how this population is shared with each partner — review defaults, spot exceptions, and update sharing in bulk"
        >
          <template #title-prefix>
            <div
              v-if="isOpenData"
              class="c-sharing-settings-population__open-data-icon"
            >
              <FontAwesomeIcon
                :icon="['fak', 'managed-offline-partner']"
                class="text-info-accent text-16"
              />
            </div>
            <BittsAvatar
              v-else
              size="small"
              shape="square"
              :show-border="true"
              :custom-svg="
                population?.integration_type
                  ? `${population?.integration_type}Icon`
                  : ''
              "
              class="c-sharing-settings-population__integration-avatar"
            />
          </template>
          <template #title-suffix>
            <BittsTag
              v-if="isOpenData"
              size="x-small"
              color="info"
              text="Open Data"
              class="mb-4"
            />
            <BittsTag
              v-else-if="population?.standard_type"
              size="x-small"
              color="info"
              text="Standard"
              class="mb-4"
            />
          </template>
          <template v-if="hasPartners && canEdit" #action>
            <BittsButton
              text="Save"
              :disabled="!hasChanges"
              :loading="isSaving"
              @click="handleSave"
            />
          </template>
        </PageTitle>
        <div class="c-sharing-settings-population">
          <PopulationTabPartnerActions
            v-if="hasPartners"
            ref="populationTabPartnerActionsRef"
            :event-site="EVENT_SITES.DEFAULT_SHARING_SINGLE_POPULATION_PAGE"
            :is-action-bar-disabled="
              isLoading ||
              (!partnersForPopulation.customPartners.length &&
                !partnersForPopulation.defaultPartners.length)
            "
            :has-edit-permission="canEdit"
            :feed-id="feedId"
            :source-id="sourceId"
            :has-custom-field-selections="hasCustomFieldSelections"
            @edit-clicked="handleEditClicked"
            @search-applied="handleSearchApplied"
            @filters-applied="handleFiltersApplied"
          />
          <div class="flex flex-col gap-12 h-full">
            <PopulationTabPartnersList
              v-show="combinedPartnersList.length"
              ref="partnersListRef"
              :partners-list="combinedPartnersList"
              :selected-partner-ids-set="selectedPartnerIdsSet"
              :partner-ids-to-restore="partnerIdsToRestore"
              :partner-ids-with-cleared-overrides="
                partnerIdsWithClearedOverrides
              "
              :feed-id="feedId"
              :source-id="sourceId"
              :can-edit="canEdit"
              :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
              :shared-base-preset-options="sharedBasePresetOptions"
              :shared-preset-fields-data="sharedPresetFieldsData"
              @partner-checkbox-change="handlePartnerCheckboxChange"
              @select-all="handleSelectAll"
              @subheader-select-all="handleSubheaderSelectAll"
              @open-partner-settings="handleOpenPartnerSettings"
              @has-changes-updated="handleHasChangesUpdated"
              @current-values-updated="updatePartnerCurrentValues"
              @restore-to-default="handleRestoreToDefault"
            />
            <div
              v-if="showEmptyState"
              class="flex items-center justify-center flex-1"
            >
              <BittsEmptyState
                :title="emptyStateConfig.title"
                :description="emptyStateConfig.description"
                font-awesome-icon-class="c-sharing-settings-population__empty-state-icon"
                :font-awesome-icon="emptyStateConfig.icon"
                :font-awesome-icon-style="{ height: '52px', width: '52px' }"
                :button="emptyStateConfig.button"
                @button-clicked="emptyStateConfig.onButtonClick"
              />
            </div>
          </div>
        </div>
      </div>
    </BittsLoading>
    <PartnershipSettingsModal
      v-if="isPartnerModalOpen && selectedPartnerForModal"
      :partner-id="selectedPartnerForModal"
      :initial-overrides-filter="initialOverridesFilter"
      @close="handleClosePartnerModal"
    />
    <PopulationSharingModal
      v-if="isPopulationSharingModalOpen"
      v-model:selected-population-ids="modalPopulationIds"
      v-model:selected-partner-ids="selectedPartnerIds"
      :is-for-partner="true"
      :is-multi-select="true"
      @close="handleClosePopulationSharingModal"
      @saved="handlePopulationSharingModalSaved"
    />
    <BittsModal
      v-if="showModal"
      variant="confirm"
      container-class="c-unsaved-changes-modal"
      :primary-button-text="modalPrimaryButtonText"
      :secondary-button-text="modalSecondaryButtonText"
      :action-loading="modalActionLoading"
      @action="handleModalPrimaryAction"
      @secondary-action="handleModalSecondaryAction"
      @closed="closeModal"
    >
      <template #image>
        <FontAwesomeIcon
          :icon="['fak', 'warning']"
          style="height: 48px; width: 48px; color: currentColor"
          class="text-warning-accent mb-16"
        />
      </template>
      <template #header>
        <div>
          <h3
            class="text-center text-neutral-text-strong font-bold text-xl mb-4"
          >
            {{ modalTitle }}
          </h3>
          <div class="text-center text-neutral-text">
            <p v-for="(line, index) in modalDescription.lines" :key="index">
              {{ line }}
            </p>
          </div>
        </div>
      </template>
    </BittsModal>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsBreadcrumbs,
  BittsButton,
  BittsEmptyState,
  BittsLoading,
  BittsModal,
  BittsTag,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';

import PageTitle from '@/components/PageTitle.vue';
import PopulationSharingModal from '@/components/partners/DataSharingSettings/PopulationSharingModal/PopulationSharingModal.vue';
import PartnershipSettingsModal from '@/components/partners/DataSharingSettings/SettingsModal/PartnershipSettingsModal.vue';

import { usePopulationPartnerRestore } from '@/composables/dataSharingHub/usePopulationPartnerRestore';
import { usePopulationPartnerSave } from '@/composables/dataSharingHub/usePopulationPartnerSave';
import { usePresetFieldsData } from '@/composables/dataSharingHub/usePresetFieldsData';
import type { ChangeTrackingState } from '@/composables/dataSharingHub/useSharingHubChangeTracking';
import { useSharingHubModal } from '@/composables/dataSharingHub/useSharingHubModal';
import { useSharingHubSelection } from '@/composables/dataSharingHub/useSharingHubSelection';
import useSharingSettingsHub, {
  type FilterValues,
  type PartnerSharingData,
  type PartnerWithSharing,
} from '@/composables/dataSharingHub/useSharingSettingsHub';
import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import useDataSharingTracking from '@/composables/useDataSharingTracking';
import { SHARING_TYPES } from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import {
  useDataSharePresetsStore,
  useDataSharesStore,
  usePartnersStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import { isOpenDataPopulation } from '@/types/populations';
import { getFeedIdForPopulation } from '@/utils/getFeedIdForPopulation';

import PopulationTabPartnerActions from './PopulationTabPartnerActions.vue';
import PopulationTabPartnersList from './PopulationTabPartnersList.vue';

/**
 * Type representing the properties exposed by PopulationTabPartnersList
 * via defineExpose. This ensures type-safe access to the component's API.
 * Note: Vue automatically unwraps refs in defineExpose, so computed refs
 * become their underlying values when accessed through template refs.
 */
type PopulationTabPartnersListExposed = {
  hasAnyChanges: boolean;
  changedCount: number;
  getAllChangedIds: () => number[];
  getAllChangedItems: () => ChangeTrackingState<number, PartnerSharingData>[];
  commitChanges: () => void;
  reset: () => void;
  getChanges: (id: number) => Partial<PartnerSharingData> | null;
  batchUpdate: (
    updates: { id: number; data: Partial<PartnerSharingData> }[],
  ) => void;
  getTrackingCount: () => number;
  enrichedPartnersList: unknown;
};

const populationsStore = usePopulationsStore();
const dataSharesStore = useDataSharesStore();
const partnersStore = usePartnersStore();
const sourcesStore = useSourcesStore();
const dataSharePresetsStore = useDataSharePresetsStore();
const { isGreenfieldSharingBlocked } = storeToRefs(dataSharesStore);

const { canEdit } = useDataSharingPermissions({ isPartnerContext: true });

// Composables for specific functionality
const {
  modalType,
  showModal,
  modalPrimaryButtonText,
  modalSecondaryButtonText,
  modalTitle,
  modalDescription,
  openUnsavedChangesModal,
  closeModal,
} = useSharingHubModal();

const {
  selectedIds: selectedPartnerIds,
  selectedIdsSet: selectedPartnerIdsSet,
  handleCheckboxChange: handlePartnerCheckboxChange,
  handleSelectAll: handleSelectAllBase,
  handleSubheaderSelectAll: handleSubheaderSelectAllBase,
  clearSelection,
} = useSharingHubSelection<PartnerWithSharing>();

const {
  partnerIdsToRestore,
  partnerIdsWithClearedOverrides,
  markForRestore,
  unmarkForRestore,
  shouldUnmarkForRestore,
  getPopulationDefaults,
  clearRestores,
} = usePopulationPartnerRestore();

const { isSaving, save } = usePopulationPartnerSave();
const { trackDataSharingChangeFromContext } = useDataSharingTracking();

// Use the composable for sharing settings logic
const {
  getPartnersForPopulation,
  filterPartners,
  hasCustomFieldSelection,
  combinePartnersWithSubheaders,
  searchValue,
  activeFilters,
  applySearch,
  applyFilters,
  updatePartnerCurrentValues: updatePartnerCurrentValuesBase,
  currentPartnerValues,
} = useSharingSettingsHub();

const partnersListRef = ref<PopulationTabPartnersListExposed | null>(null);

const isLoading = ref(true);
const route = useRoute();
const router = useRouter();
const populationId = computed(() =>
  parseInt(route.params.population_id as string),
);
const population = computed(() => {
  return populationsStore.getPopulationById(populationId.value);
});
const isOpenData = computed(() => isOpenDataPopulation(population.value));

const feedId = computed(() =>
  getFeedIdForPopulation(population.value ?? undefined, (id) =>
    sourcesStore.getSourceById(id),
  ),
);

const sourceId = computed(() => {
  if (!population.value) return undefined;
  const isGoogleOrCsv = isGoogleOrCsvSource(population.value.integration_type);
  return isGoogleOrCsv ? population.value.source_id : undefined;
});

const sharedBasePresetOptions = computed(() => {
  if (!feedId.value) return [];

  const currentFeedId = feedId.value;
  const filteredPresets = dataSharePresetsStore.getFilteredPresetsByFeeId(
    currentFeedId,
    sourceId.value,
  );

  const allOptions =
    dataSharePresetsStore.getPresetOptionsPerFeed(filteredPresets);
  const parentId = sourceId.value ?? currentFeedId;
  return allOptions[parentId] ?? [];
});

const { presetFieldsData: sharedPresetFieldsData } = usePresetFieldsData();

const breadcrumbs = computed(() => [
  {
    name: 'Sharing Settings',
    text: 'Sharing Settings',
  },
  {
    name: population.value?.name ?? '',
    text: population.value?.name ?? '',
  },
]);

const hasChanges = ref(false);

const modalActionLoading = computed(() => isSaving.value);

/**
 * Wrapper for updatePartnerCurrentValues that handles restoration state.
 * If a partner is marked for restoration but the user changes its values,
 * we remove it from the restore set (they're making a new custom change).
 */
function updatePartnerCurrentValues(
  partnerId: number,
  values: PartnerSharingData,
) {
  updatePartnerCurrentValuesBase(partnerId, values);

  const currentValues = currentPartnerValues.value.get(partnerId);

  // If values differ from defaults, unmark from restore so the new changes get saved
  // The override will still be deleted because it's tracked in partnerIdsWithClearedOverrides
  if (
    currentValues &&
    shouldUnmarkForRestore(partnerId, populationId.value, currentValues)
  ) {
    unmarkForRestore(partnerId);
  }
}

function handleHasChangesUpdated(hasChangesValue: boolean) {
  hasChanges.value =
    hasChangesValue ||
    partnerIdsToRestore.value.size > 0 ||
    partnerIdsWithClearedOverrides.value.size > 0;
}

async function handleModalPrimaryAction() {
  await handleSaveAndNavigate();
}

async function handleModalSecondaryAction() {
  await handleDiscardChanges();
}

async function handleSave() {
  const allChangedItems: ChangeTrackingState<number, PartnerSharingData>[] =
    partnersListRef.value?.getAllChangedItems() ?? [];

  const defaultRule = dataSharesStore.getOutgoingSharingRules({
    ruleType: 'population',
    populationId: populationId.value,
  })[0];
  const defaultVisibility = defaultRule?.visibility ?? SHARING_TYPES.HIDDEN;
  const defaultPresetId = defaultRule?.data_share_presets_id ?? null;

  const restoredPartnersPrevious = new Map<
    number,
    { visibility: string; presetId: number | null }
  >();
  partnerIdsToRestore.value.forEach((partnerId) => {
    const partners = getPartnersForPopulation(populationId.value).allPartners;
    const partner = partners.find((p) => p.id === partnerId);
    if (partner) {
      restoredPartnersPrevious.set(partnerId, {
        visibility: partner.sharingRule?.visibility ?? defaultVisibility,
        presetId: partner.presetInfo?.presetId ?? null,
      });
    }
  });

  const success = await save(
    populationId.value,
    allChangedItems,
    partnerIdsToRestore.value,
    partnerIdsWithClearedOverrides.value,
  );

  if (!success) return false;

  const changedItemsToTrack = allChangedItems.filter(
    (item) =>
      !partnerIdsToRestore.value.has(item.id as number) &&
      item.current.visibility !== undefined,
  );
  changedItemsToTrack.forEach((item) => {
    trackDataSharingChangeFromContext({
      population: population.value ?? undefined,
      current: {
        visibility: item.current.visibility ?? '',
        presetId: item.current.presetId ?? null,
      },
      previous: {
        visibility: item.original.visibility,
        presetId: item.original.presetId ?? null,
      },
      eventSite: EVENT_SITES.DEFAULT_SHARING_SINGLE_POPULATION_PAGE,
      bulk: false,
      partnerId: item.id,
    });
  });

  partnerIdsToRestore.value.forEach((partnerId) => {
    const previous = restoredPartnersPrevious.get(partnerId);
    if (previous) {
      trackDataSharingChangeFromContext({
        population: population.value ?? undefined,
        current: {
          visibility: defaultVisibility,
          presetId: defaultPresetId,
        },
        previous: {
          visibility: previous.visibility,
          presetId: previous.presetId,
        },
        eventSite: EVENT_SITES.DEFAULT_SHARING_SINGLE_POPULATION_PAGE,
        bulk: false,
        partnerId,
      });
    }
  });

  // Clear all current partner values after successful save
  // Changes have been committed, so we should use the updated original values
  currentPartnerValues.value = new Map();

  partnersListRef.value?.commitChanges();
  clearRestores();
  hasChanges.value = false;

  return true;
}

function handleBreadcrumbClick({ name }: { name: string }) {
  switch (name) {
    case 'Sharing Settings':
      router.push({ name: 'sharing-settings' });
      break;
  }
}

// Get partners for this population from the composable
const partnersForPopulation = computed(() => {
  return getPartnersForPopulation(populationId.value);
});

const hasCustomFieldSelections = computed(() =>
  hasCustomFieldSelection(partnersForPopulation.value.allPartners),
);

const hasPartners = computed(() => {
  return (
    partnersForPopulation.value.customPartners.length > 0 ||
    partnersForPopulation.value.defaultPartners.length > 0
  );
});

const showEmptyState = computed(() => {
  return !combinedPartnersList.value.length;
});

const emptyStateConfig = computed(() => {
  if (!hasPartners.value) {
    return {
      title: 'No partners yet',
      description:
        'Default sharing settings are configured per population. Add partners to define how data is shared with partners for this population.',
      icon: ['fak', 'partner'] as ['fak', string],
      button: {
        text: 'Partners',
        size: 'small' as const,
        variant: 'outline' as const,
        type: 'primary' as const,
      },
      onButtonClick: handleGoToPartners,
    };
  }

  return {
    title: 'No results',
    description: 'Try different filters to find your partners',
    icon: ['fak', 'search'] as ['fak', string],
    button: {
      text: 'Clear Filters',
      size: 'small' as const,
      variant: 'outline' as const,
      type: 'primary' as const,
    },
    onButtonClick: handleClearFilters,
  };
});

// Partner modal state
const isPartnerModalOpen = ref(false);
const selectedPartnerForModal = ref<number | null>(null);
const initialOverridesFilter = ref<boolean | undefined>();

// Population sharing modal state
const isPopulationSharingModalOpen = ref(false);
const modalPopulationIds = ref<number[]>([populationId.value]);

const combinedPartnersList = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  currentPartnerValues.value;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  partnerIdsToRestore.value.size;

  // Get the population's default sharing rule
  const defaultRule = dataSharesStore.getOutgoingSharingRules({
    ruleType: 'population',
    populationId: populationId.value,
  })[0];

  const defaultVisibility = defaultRule?.visibility ?? SHARING_TYPES.HIDDEN;
  const defaultPresetId = defaultRule?.data_share_presets_id ?? null;

  const customPartners: PartnerWithSharing[] = [];
  const defaultPartners: PartnerWithSharing[] = [];

  partnersForPopulation.value.allPartners.forEach((partner) => {
    const currentValues = currentPartnerValues.value.get(partner.id);

    const originalVisibility = partner.sharingRule?.visibility;
    const originalPresetId = partner.presetInfo?.presetId ?? null;

    const finalVisibility =
      currentValues?.visibility ?? originalVisibility ?? defaultVisibility;
    const finalPresetId =
      currentValues?.presetId ?? originalPresetId ?? defaultPresetId;

    const matchesDefaults =
      finalVisibility === defaultVisibility &&
      finalPresetId === defaultPresetId;

    const hasOverrides = (partner.overridesCount ?? 0) > 0;
    const isMarkedForRestore = partnerIdsToRestore.value.has(partner.id);
    const hasOverridesCleared = partnerIdsWithClearedOverrides.value.has(
      partner.id,
    );

    // A partner should be in the custom section if:
    // 1. It has overrides marked for clearing AND values don't match defaults (user changed after restore), OR
    // 2. It's not marked for restore AND (doesn't match defaults OR has overrides)
    const shouldBeInCustomSection =
      (hasOverridesCleared && !matchesDefaults) ||
      (!isMarkedForRestore && (!matchesDefaults || hasOverrides));

    const updatedPartner = {
      ...partner,
      isCustom: shouldBeInCustomSection,
      // Force overridesCount to 0 if marked for restore OR if overrides have been cleared
      overridesCount:
        isMarkedForRestore || hasOverridesCleared ? 0 : partner.overridesCount,
    };

    if (shouldBeInCustomSection) {
      customPartners.push(updatedPartner);
    } else {
      defaultPartners.push(updatedPartner);
    }
  });

  const customFiltered = filterPartners(
    customPartners,
    searchValue.value,
    activeFilters.value,
  );

  const defaultFiltered = filterPartners(
    defaultPartners,
    searchValue.value,
    activeFilters.value,
  );

  return combinePartnersWithSubheaders(customFiltered, defaultFiltered);
});

function handleEditClicked() {
  isPopulationSharingModalOpen.value = true;
}

function handleClosePopulationSharingModal() {
  isPopulationSharingModalOpen.value = false;
}

function handlePopulationSharingModalSaved() {
  clearSelection();
  isPopulationSharingModalOpen.value = false;
}

function handleSearchApplied(value: string) {
  applySearch(value);
}

function handleFiltersApplied(filterValues: FilterValues) {
  applyFilters(filterValues);
}

const populationTabPartnerActionsRef = ref<InstanceType<
  typeof PopulationTabPartnerActions
> | null>(null);

function handleClearFilters() {
  searchValue.value = '';
  activeFilters.value = {};
  populationTabPartnerActionsRef.value?.clearAllFilters();
}

function handleGoToPartners() {
  router.push({ name: 'partners' });
}

function handleSelectAll(checked: boolean) {
  handleSelectAllBase(checked, combinedPartnersList.value);
}

function handleSubheaderSelectAll(sectionId: string, checked: boolean) {
  handleSubheaderSelectAllBase(sectionId, checked, combinedPartnersList.value);
}

function handleOpenPartnerSettings(
  partnerId: number,
  overridesFilter?: boolean,
) {
  selectedPartnerForModal.value = partnerId;
  initialOverridesFilter.value = overridesFilter;
  isPartnerModalOpen.value = true;
}

function handleClosePartnerModal() {
  isPartnerModalOpen.value = false;
  selectedPartnerForModal.value = null;
  initialOverridesFilter.value = undefined;
}

function handleRestoreToDefault(partnerId: number) {
  const population = populationsStore.getPopulationById(populationId.value);
  if (!population) return;

  const { visibility: defaultVisibility, presetId: defaultPresetId } =
    getPopulationDefaults(populationId.value);

  // Check if this partner has overrides before updating
  const partner = partnersForPopulation.value.allPartners.find(
    (p: PartnerWithSharing) => p.id === partnerId,
  );
  const hadOverrides = (partner?.overridesCount ?? 0) > 0;

  // Update values to defaults first
  partnersListRef.value?.batchUpdate([
    {
      id: partnerId,
      data: {
        visibility: defaultVisibility,
        presetId: defaultPresetId,
      },
    },
  ]);

  updatePartnerCurrentValues(partnerId, {
    visibility: defaultVisibility,
    presetId: defaultPresetId,
  });

  // Check if this results in a value change from the original state
  const hasActualChanges = partnersListRef.value?.getChanges(partnerId);
  const hasValueChanges =
    hasActualChanges && Object.keys(hasActualChanges).length > 0;

  // If the partner had overrides, we need to mark it for override clearing
  // even if the values happen to match the defaults, because we're deleting the override
  if (hasValueChanges || hadOverrides) {
    markForRestore(partnerId);
    hasChanges.value = true;
  } else {
    // If no changes and no overrides, make sure the partner isn't marked for restore
    unmarkForRestore(partnerId);
    // Update hasChanges based on whether there are any other changes
    const partnerListHasChanges = partnersListRef.value?.hasAnyChanges ?? false;
    hasChanges.value =
      partnerListHasChanges ||
      partnerIdsToRestore.value.size > 0 ||
      partnerIdsWithClearedOverrides.value.size > 0;
  }
}

async function handleSaveAndNavigate() {
  const success = await handleSave();
  if (!success) return;

  const routeToNavigate = closeModal();
  if (routeToNavigate) {
    await router.push(routeToNavigate);
  }
}

async function handleDiscardChanges() {
  // Clear all current partner values, not just those marked for restore
  // This ensures stale entries don't cause partners to appear in the wrong section
  currentPartnerValues.value = new Map();

  partnersListRef.value?.reset();
  clearRestores();
  hasChanges.value = false;

  const routeToNavigate = closeModal();
  if (routeToNavigate) {
    await router.push(routeToNavigate);
  }
}

onBeforeMount(async () => {
  await Promise.all([
    populationsStore.readySync,
    partnersStore.readySync,
    dataSharesStore.readySync,
    dataSharePresetsStore.readySync,
  ]);
  if (!population.value) router.push({ name: 'sharing-settings' });
  isLoading.value = false;
});

onBeforeRouteLeave((to, _from, next) => {
  if (!modalType.value && hasChanges.value) {
    openUnsavedChangesModal(to);
    next(false);
  } else {
    next();
  }
});

watch(populationId, (id) => {
  modalPopulationIds.value = [id];
});
</script>
<style scoped lang="pcss">
@reference "../../../css/theme.css";
:deep(.c-sharing-settings-population__integration-avatar) {
  span {
    @apply bg-white! mb-4;
  }
}

.c-sharing-settings-population__open-data-icon {
  @apply size-[24px] rounded-[4px] border border-solid border-neutral-border
    bg-white flex items-center justify-center shrink-0 mb-4;
}

.c-sharing-settings-population {
  @apply flex flex-col gap-20 h-full;

  :deep(.c-loading) {
    @apply flex-1;
  }
}

:deep(.c-sharing-settings-population__empty-state-icon) {
  @apply text-neutral-text-weak p-[26px] rounded-[52px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      color-mix(in srgb, theme(colors.neutral-accent) 20%, transparent),
      color-mix(in srgb, theme(colors.neutral-accent) 0%, transparent)
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}

:deep(.c-unsaved-changes-modal .o-bitts-modal__header) {
  @apply !px-24 !py-40 !m-0;
}
</style>
